import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { GemIcon } from '@sorare/core/src/atoms/icons/GemIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, LabelL } from '@sorare/core/src/atoms/typography';
import { TransactionEntry } from '@sorare/core/src/components/mySorare/TransactionsHistory/common/TransactionEntry';
import { EmptyContent } from '@sorare/core/src/contexts/intl/EmptyContent';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { glossary } from '@sorare/core/src/lib/glossary';

import type {
  GemsTransactionsQuery,
  GemsTransactionsQueryVariables,
} from './__generated__/index.graphql.ts';

const messages = defineMessages({
  empty: {
    defaultMessage: 'No Gems Transactions',
  },
});

const QUERY = gql`
  query GemsTransactionsQuery($after: String) {
    currentUser {
      slug
      commonGemHistoryTransactions(after: $after) {
        nodes {
          id
          amount
          createdAt
          label
          pictureUrl
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
` as TypedDocumentNode<GemsTransactionsQuery, GemsTransactionsQueryVariables>;

const GemsAmount = ({ amount }: { amount: number }) => {
  return (
    <Horizontal gap={0.5}>
      <LabelL
        bold
        color={
          amount >= 0 ? 'var(--c-onBase-success)' : 'var(--c-onBase-error)'
        }
      >
        {amount}
      </LabelL>

      <GemIcon />
    </Horizontal>
  );
};

export const GemsTransactions = () => {
  const { data, loading, loadMore } = usePaginatedQuery(QUERY, {
    connection: 'InGameCurrencyTransactionConnection',
  });
  const paginatedResults = data?.currentUser?.commonGemHistoryTransactions;

  return (
    <Vertical gap={2}>
      <BrandM>
        <FormattedMessage defaultMessage="My activity" />
      </BrandM>
      <div>
        {paginatedResults?.nodes?.map(transaction => (
          <TransactionEntry
            key={transaction.id}
            title={transaction.label}
            date={transaction.createdAt}
            expandable={false}
            subtitle={
              transaction.pictureUrl ? (
                <img
                  src={transaction.pictureUrl}
                  alt={transaction.label}
                  height={24}
                />
              ) : undefined
            }
            amount={<GemsAmount amount={transaction.amount} />}
          />
        ))}
      </div>
      {paginatedResults?.nodes?.length === 0 && !loading && (
        <EmptyContent message={messages.empty} />
      )}
      {!!paginatedResults?.pageInfo?.hasNextPage &&
        !!paginatedResults?.pageInfo?.endCursor && (
          <Button
            size="small"
            onClick={async () =>
              loadMore(false, {
                after: paginatedResults.pageInfo.endCursor,
              })
            }
            disabled={loading}
          >
            <FormattedMessage {...glossary.showMore} />
          </Button>
        )}
    </Vertical>
  );
};
